<template>
  <div
    v-click-outside="onClickOutside"
    class="supplier-search-machine"
  >
    <div class="header-wrapper">
      <label class="label">
        Supplier
      </label>
      <span
        v-if="showRequired"
        class="required-label"
      >(required)</span>
      <router-link
        aria-label="Create New Supplier (opens in a new window)"
        to="/supplier/0"
        class="create-new"
        target="_blank"
      >
        Create
      </router-link>
    </div>
    <CloseSvg
      v-if="modelValue && modelValue !== ''"
      class="close"
      @click="onClearClicked"
    />
    <TextInput
      v-if="modelValue && modelValue !== ''"
      v-model="supplierName"
      :disabled="true"
    />
    <div
      v-else
      class="text-input-label"
    >
      <input
        v-model="searchTerm"
        class="text-input"
        aria-label="Search"
        :class="{'flatten-bottom': showResults}"
        placeholder="Search"
        autocomplete="off"
        :aria-required="showRequired"
        @input="onSearchTermChanged"
        @keyup.down="onDownArrowPressed"
        @keyup.up="onUpArrowPressed"
        @keyup.enter.stop="onEnterPressed"
        @keydown.tab="onClickOutside"
      >
      <div
        v-if="showResults"
        class="results-container"
      >
        <LoadingPulse
          :loading="searching"
          class="loading-spinner"
        />
        <div
          v-if="!searching"
          class="results"
        >
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="result-option"
            :class="result.highlighted ? 'highlighted' : ''"
            @click="chooseOption(result)"
          >
            <div class="result-option-name">
              {{ result.name }}
            </div>
            <div class="result-option-email">
              {{ result.email }}
            </div>
            <div class="result-option-phone">
              {{ $methods.formatPhoneNumber(result.phoneNumber) }}
            </div>
          </div>
          <div
            v-if="searchResults.length == 0"
            class="no-results-container"
          >
            <div class="no-results">
              No results
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { SupplierService } from '@/services/supplier.service'
import { defineComponent, inject } from 'vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { Supplier } from '@/@types/responses/supplier/Supplier'
import Toaster from '@meforma/vue-toaster'
import TextInput from '@/components/shared/TextInput.vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'SupplierSearchMachine',
  components: {
    CloseSvg,
    LoadingPulse,
    TextInput
  },
  props: {
    modelValue: {
      type: String,
      required: true
    },
    showRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup() {
    const supplierService = inject(PROVIDER_CONSTANTS.supplierService) as SupplierService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function createDebounce() {
      let timeout: number|undefined = undefined
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      return function (func: any, delay?: number|undefined) {
        clearTimeout(timeout)
        timeout = setTimeout(() => { func() }, delay || 500)
      }
    }
    return {
      debounce: createDebounce(),
      supplierService,
      toast
    }
  },
  data() {
    let supplier: Supplier|undefined

    return {
      searching: false,
      searchTerm: '',
      searchResults: [] as Supplier[],
      supplier
    }
  },
  computed: {
    showResults(): boolean {
      return this.searchTerm !== '';
    },
    supplierName(): string {
      if(!this.supplier) return ''

      return this.supplier.name
    },
  },
  mounted() {
    if (this.modelValue && this.modelValue !== '')
      this.supplierService.getSupplier(Number(this.modelValue)).then(supplier => {
        this.supplier = new Supplier(supplier.id, supplier.name, supplier.phoneNumber, supplier.email, supplier.website)
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load your supplier. Try reloading the page.')
        }
      })
  },
  methods: {
    chooseOption(supplier: Supplier): void {
      this.searchTerm = ''
      this.supplier = supplier
      this.$emit('update:modelValue', supplier.id.toString())
    },
    getHighlightedSupplier(): Supplier|undefined {
      const highlightedSupplier = this.searchResults.filter(s => s.highlighted);
      return highlightedSupplier.length > 0 ? highlightedSupplier[0] : undefined;
    },
    onClearClicked(): void {
      this.$emit('update:modelValue', '')
    },
    onClickOutside(): void {
      this.searchTerm = ''
    },
    onDownArrowPressed(): void {
      const highlightedSupplier = this.getHighlightedSupplier();

      let nextIndex = highlightedSupplier ? this.searchResults.indexOf(highlightedSupplier) + 1 : 0;
      if (nextIndex > this.searchResults.length - 1)
        nextIndex = 0;
      
      if (highlightedSupplier)
        highlightedSupplier.highlighted = false;

      this.searchResults[nextIndex].highlighted = true;
    },
    onEnterPressed(): void {
      const highlightedSupplier = this.getHighlightedSupplier();
      if (highlightedSupplier)
        this.chooseOption(highlightedSupplier)
    },
    onSearchTermChanged(): void {
      if (this.searchTerm !== '') {
        this.searching = true
        this.debounce(this.searchSuppliers)
      }
    },
    onUpArrowPressed(): void {
      const highlightedSupplier = this.getHighlightedSupplier();
      let previousIndex = highlightedSupplier ? this.searchResults.indexOf(highlightedSupplier) - 1 : 0;

      if (previousIndex < 0)
        previousIndex = this.searchResults.length - 1;

      if (highlightedSupplier)
        highlightedSupplier.highlighted = false;

      this.searchResults[previousIndex].highlighted = true;
    },
    searchSuppliers(): void {
      this.supplierService.searchSuppliers(this.searchTerm, 1, 25, 'Name', true).then(pagingResponse => {
        this.searchResults = pagingResponse.results
        this.searching = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error(`Failed to search for your suppliers. Please try again.`)
        }
      })
    }
  }
})
</script>

<style scoped lang="scss">
.supplier-search-machine {
  @include sac-search-machine;
  position: relative;

  > .close {
    position: absolute;
    right: 7px;
    top: 38px;
  }
}
</style>